/** Responsive auto-fill card grid with FLIP layout animations. */
import type { ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useDataView } from './DataView';

interface DataViewCardsProps<T> {
  renderCard: (item: T) => ReactNode;
  /** Minimum card width in px for auto-fill grid. @default 280 */
  cardMinWidth?: number;
  className?: string;
}

export function DataViewCards<T>({
  renderCard,
  cardMinWidth = 280,
  className,
}: DataViewCardsProps<T>) {
  const { items, getItemKey, onItemClick } = useDataView<T>();

  return (
    <div
      className={className}
      style={{
        display: 'grid',
        gap: '0.75rem',
        gridTemplateColumns: `repeat(auto-fill, minmax(${cardMinWidth}px, 1fr))`,
      }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {items.map((item) => (
          <motion.div
            key={getItemKey(item)}
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{
              layout: { type: 'spring', stiffness: 400, damping: 30 },
              opacity: { duration: 0.2 },
              scale: { duration: 0.2 },
            }}
            onClick={onItemClick ? () => onItemClick(item) : undefined}
            onKeyDown={
              onItemClick
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onItemClick(item);
                    }
                  }
                : undefined
            }
            role={onItemClick ? 'button' : undefined}
            tabIndex={onItemClick ? 0 : undefined}
            className="cursor-pointer rounded-2xl border border-[var(--f-border)] bg-[var(--f-bg-raised)] outline-none transition-[box-shadow,border-color] duration-150 hover:border-[var(--f-border-strong)] hover:shadow-[var(--f-shadow-sm)] focus-visible:ring-2 focus-visible:ring-[var(--f-accent)] focus-visible:ring-offset-1"
          >
            {renderCard(item)}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
